import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveKeywordRankingsSnapshot, fetchSearchConsoleQuery } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveContainer, LineChart, Line, YAxis } from "recharts";
import { TrendingUp, TrendingDown, Minus, Plus, Target, ChevronRight, Calendar, Search, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

function trendIcon(delta) {
  if (delta == null || delta === 0) return { Icon: Minus, tone: "text-slate-400 bg-slate-50 border-slate-200", label: "0" };
  if (delta > 0) return { Icon: TrendingUp, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: `↑ ${delta}` };
  return { Icon: TrendingDown, tone: "text-rose-700 bg-rose-50 border-rose-200", label: `↓ ${Math.abs(delta)}` };
}

function posBadge(pos) {
  if (pos == null) return { tone: "bg-slate-100 text-slate-500", label: "—" };
  if (pos <= 3) return { tone: "bg-emerald-100 text-emerald-800", label: `#${pos}` };
  if (pos <= 10) return { tone: "bg-sky-100 text-sky-800", label: `#${pos}` };
  if (pos <= 30) return { tone: "bg-amber-100 text-amber-800", label: `#${pos}` };
  return { tone: "bg-slate-100 text-slate-700", label: `#${pos}` };
}

function googlePage(pos) {
  if (pos == null) return null;
  const page = Math.ceil(pos / 10);
  if (page === 1) return { tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Pág 1" };
  if (page === 2) return { tone: "text-sky-700 bg-sky-50 border-sky-200", label: "Pág 2" };
  if (page === 3) return { tone: "text-amber-700 bg-amber-50 border-amber-200", label: "Pág 3" };
  return { tone: "text-slate-600 bg-slate-50 border-slate-200", label: `Pág ${page}` };
}

export default function KeywordRankingsCard({ clientId, rows, kwList, snapshotDate, gscSiteUrl }) {
  const [open, setOpen] = useState(false);

  // Counts for the header KPIs
  const summary = useMemo(() => {
    let top3 = 0, top10 = 0, top30 = 0, improved = 0, declined = 0, total = rows.length;
    for (const r of rows) {
      if (r.last_pos != null) {
        if (r.last_pos <= 3) top3++;
        if (r.last_pos <= 10) top10++;
        if (r.last_pos <= 30) top30++;
      }
      if (r.delta > 0) improved++;
      else if (r.delta < 0) declined++;
    }
    return { top3, top10, top30, improved, declined, total };
  }, [rows]);

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="keyword-rankings-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3 flex-wrap">
        <Target className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Posicionamiento de keywords</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Rankings actuales vs Día 0 · {rows.length} keyword{rows.length === 1 ? "" : "s"} en seguimiento</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="open-update-keywords-dialog"
          className="shrink-0"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Actualizar posiciones
        </Button>
      </div>

      {/* Summary chips */}
      {rows.length > 0 && (
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-4 flex-wrap text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-slate-500">Top 3:</span>
            <span className="font-mono font-semibold text-slate-900">{summary.top3}/{summary.total}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-sky-500" />
            <span className="text-slate-500">Top 10:</span>
            <span className="font-mono font-semibold text-slate-900">{summary.top10}/{summary.total}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-slate-500">Top 30:</span>
            <span className="font-mono font-semibold text-slate-900">{summary.top30}/{summary.total}</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-1 text-emerald-700"><TrendingUp className="h-3 w-3" /> {summary.improved} suben</div>
            <div className="flex items-center gap-1 text-rose-700"><TrendingDown className="h-3 w-3" /> {summary.declined} bajan</div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center" data-testid="keyword-rankings-empty">
          <Search className="h-6 w-6 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500 mb-1">Todavía no cargaste posiciones</p>
          <p className="text-xs text-slate-400 mb-4">
            {kwList.length > 0
              ? <>Ya tenés {kwList.length} keyword{kwList.length === 1 ? "" : "s"} configurada{kwList.length === 1 ? "" : "s"} en Compromiso. Apretá <span className="font-medium">Actualizar posiciones</span> para registrar el punto de partida.</>
              : <>Antes de empezar, cargá las keywords KPI desde el botón <span className="font-medium">Configurar</span> de la pestaña.</>
            }
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {/* Header */}
          <div className="hidden md:grid grid-cols-[1fr_80px_80px_70px_70px_120px_90px] gap-3 px-5 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <div>Keyword</div>
            <div className="text-center">Día 0</div>
            <div className="text-center">Actual</div>
            <div className="text-center">Página</div>
            <div className="text-center">Δ</div>
            <div>Evolución</div>
            <div className="text-right">Último update</div>
          </div>
          {/* Rows */}
          {rows.map((r) => {
            const t = trendIcon(r.delta);
            const first = posBadge(r.first_pos);
            const last = posBadge(r.last_pos);
            const page = googlePage(r.last_pos);
            return (
              <div key={r.kw} className="grid grid-cols-[1fr] md:grid-cols-[1fr_80px_80px_70px_70px_120px_90px] gap-3 px-5 py-3 items-center hover:bg-slate-50/60 transition-colors" data-testid="keyword-row">
                <div className="font-medium text-sm text-slate-900 truncate" title={r.kw}>{r.kw}</div>
                <div className="flex justify-center">
                  <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${first.tone}`}>{first.label}</span>
                </div>
                <div className="flex justify-center">
                  <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${last.tone}`}>{last.label}</span>
                </div>
                <div className="flex justify-center">
                  {page ? (
                    <span className={`font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded border ${page.tone}`}>{page.label}</span>
                  ) : (
                    <span className="text-[10px] text-slate-400">—</span>
                  )}
                </div>
                <div className="flex justify-center">
                  <span className={`inline-flex items-center gap-0.5 font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded border ${t.tone}`}>
                    {t.label}
                  </span>
                </div>
                <div className="h-8">
                  {r.history.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={r.history} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                        <YAxis reversed hide domain={["dataMin - 1", "dataMax + 1"]} />
                        <Line
                          type="monotone"
                          dataKey="position"
                          stroke={r.delta > 0 ? "#10b981" : r.delta < 0 ? "#f43f5e" : "#94a3b8"}
                          strokeWidth={1.5}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center text-[10px] text-slate-400 italic">Sin histórico</div>
                  )}
                </div>
                <div className="text-right text-[10px] text-slate-500 font-mono tabular-nums">{r.last_date || "—"}</div>
              </div>
            );
          })}
        </div>
      )}

      <UpdateKeywordPositionsDialog
        open={open}
        onOpenChange={setOpen}
        clientId={clientId}
        kwList={kwList}
        currentRows={rows}
        snapshotDate={snapshotDate}
        gscSiteUrl={gscSiteUrl}
      />
    </section>
  );
}

function UpdateKeywordPositionsDialog({ open, onOpenChange, clientId, kwList, currentRows, snapshotDate, gscSiteUrl }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [positions, setPositions] = useState({});
  const [siteUrl, setSiteUrl] = useState(gscSiteUrl || "");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-fill with the latest known position for each kw (so user can edit incrementally)
    const init = {};
    for (const kw of kwList) {
      const row = currentRows.find((r) => r.kw === kw);
      init[kw] = row?.last_pos != null ? String(row.last_pos) : "";
    }
    setPositions(init);
    setSiteUrl(gscSiteUrl || "");
    // If user hasn't taken Day 0 yet (no rows), default the date to snapshot_date
    if ((currentRows || []).length === 0 && snapshotDate) {
      setDate(snapshotDate);
    } else {
      setDate(today);
    }
  }, [open]);

  const handleImportFromSearchConsole = async () => {
    if (!siteUrl.trim()) {
      toast.error("Pegá la URL del sitio en Search Console (ej. sc-domain:example.com)");
      return;
    }
    if (kwList.length === 0) {
      toast.error("Cargá primero las keywords KPI en Configurar compromiso");
      return;
    }
    setImporting(true);
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 28);
      const resp = await fetchSearchConsoleQuery({
        site_url: siteUrl.trim(),
        start_date: start.toISOString().slice(0, 10),
        end_date: end.toISOString().slice(0, 10),
        dimensions: ["query"],
        row_limit: 25000,
      });
      // resp.rows = [{keys:[query], clicks, impressions, ctr, position}]
      const byQuery = new Map();
      for (const r of (resp.rows || [])) {
        const q = (r.keys?.[0] || "").toLowerCase();
        if (q && r.position) byQuery.set(q, Math.round(r.position));
      }
      const next = { ...positions };
      let matched = 0;
      for (const kw of kwList) {
        const pos = byQuery.get(kw.toLowerCase());
        if (pos) {
          next[kw] = String(pos);
          matched++;
        }
      }
      setPositions(next);
      if (matched === 0) {
        toast.warning(`Search Console respondió pero ninguna keyword KPI matcheó (probá con sc-domain:tudominio.com o https://tudominio.com/).`);
      } else {
        toast.success(`${matched}/${kwList.length} keywords importadas de Search Console`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Error al consultar Search Console";
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  };

  const mut = useMutation({
    mutationFn: () => {
      const cleaned = {};
      for (const [kw, val] of Object.entries(positions)) {
        const n = parseInt(val, 10);
        if (Number.isFinite(n) && n > 0) cleaned[kw] = n;
      }
      return saveKeywordRankingsSnapshot(clientId, { date, positions: cleaned });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["seo", clientId] });
      toast.success("Posiciones actualizadas");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "No se pudo guardar"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="update-keywords-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Actualizar posiciones de keywords</DialogTitle>
          <DialogDescription>
            Ingresá la posición actual (#1, #2, #3…) para cada keyword. También podés importar automáticamente desde Google Search Console pegando el site URL.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Fecha del snapshot</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 h-9 text-sm"
              data-testid="update-keywords-date"
            />
          </div>

          <div className="bg-indigo-50/60 border border-indigo-100 rounded-md p-3">
            <Label className="text-xs flex items-center gap-1.5 text-indigo-800 font-semibold">
              <Download className="h-3 w-3" /> Importar desde Search Console
            </Label>
            <p className="text-[10px] text-indigo-700/80 mt-0.5 mb-2 leading-snug">
              Pegá el site URL (ej. <span className="font-mono">sc-domain:tudominio.com</span> o <span className="font-mono">https://tudominio.com/</span>). Promedio de posición últimos 28 días.
            </p>
            <div className="flex gap-2">
              <Input
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="sc-domain:tudominio.com"
                className="h-9 text-xs font-mono flex-1"
                data-testid="gsc-site-url-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleImportFromSearchConsole}
                disabled={importing || !siteUrl.trim() || kwList.length === 0}
                data-testid="gsc-import-btn"
                className="shrink-0"
              >
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                Importar
              </Button>
            </div>
          </div>

          {kwList.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500">
              Primero cargá keywords KPI desde <span className="font-medium">Configurar</span>.
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {kwList.map((kw) => (
                <div key={kw} className="grid grid-cols-[1fr_90px] gap-3 items-center">
                  <span className="text-sm text-slate-800 truncate" title={kw}>{kw}</span>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={positions[kw] || ""}
                    onChange={(e) => setPositions((p) => ({ ...p, [kw]: e.target.value }))}
                    placeholder="#?"
                    className="h-9 text-sm font-mono"
                    data-testid={`update-kw-input-${kw}`}
                  />
                </div>
              ))}
              <p className="text-[10px] text-slate-400 leading-snug pt-2">
                Si dejás vacía una keyword, su posición no se actualiza en esta fecha (no se borra el histórico).
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mut.isPending}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || kwList.length === 0} data-testid="save-keywords-btn">
            {mut.isPending ? "Guardando…" : "Guardar snapshot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
